import type { DynamicFilterValue } from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-values";
import {
    isDynamicFilterValueEmpty,
    parseDynamicFilterValue,
} from "app/pages/sales-intelligence/domain/search/utils/dynamic-filter-values";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import type { CrmFilterItem, CrmFiltersList, CrmFiltersModel } from "../types/custom-filter-values";
import type { CrmIntegrationProvider } from "../types/provider";

import {
    bareCrmConditionKey,
    crmConditionInclusion,
    crmConditionVariantKey,
    crmFilterKeyPrefix,
    isCrmFieldKey,
    toCrmFilterKey,
} from "./crm-filter-key";

const INCLUSION_FIELD = "inclusion";

/**
 * "Does this flat panel carry any non-empty CRM field for the provider?" — the presence-based "applied" signal
 * that replaces the removed apply toggle (SI-8738). Loosely typed so flat panel maps pass without a cast.
 */
export const hasAppliedCrmFields = (
    flat: Readonly<Record<string, unknown>>,
    provider: CrmIntegrationProvider,
): boolean =>
    Object.entries(flat).some(([key, value]) => {
        if (!isCrmFieldKey(provider, key)) {
            return false;
        }

        const filterValue = parseDynamicFilterValue(value);

        return !!filterValue && !isDynamicFilterValueEmpty(filterValue);
    });

/**
 * Flat panel → request array (SI-8738). Each non-empty condition key becomes one item carrying its inclusion
 * (derived from the key shape) and its single BARE field entry — the `:$exclude` suffix never reaches the wire.
 * A field with both sides applied yields two items; items AND together.
 */
export const crmFieldsToModel = (
    fields: Readonly<CrmFiltersList>,
    provider: CrmIntegrationProvider,
): CrmFiltersModel => {
    const prefix = crmFilterKeyPrefix(provider);

    return Object.entries(fields).reduce<CrmFilterItem[]>((items, [key, value]) => {
        if (!isCrmFieldKey(provider, key)) {
            return items;
        }

        const filterValue = parseDynamicFilterValue(value);
        if (!filterValue || isDynamicFilterValueEmpty(filterValue)) {
            return items;
        }

        const bareField = bareCrmConditionKey(key).slice(prefix.length);

        items.push({ inclusion: crmConditionInclusion(key), [bareField]: filterValue });

        return items;
    }, []);
};

/**
 * Request array → flat panel. Mirrors BE (MR !26607), which reads a legacy object payload as null: a non-array
 * expands to empty. Each item maps to the condition key its inclusion encodes; two same-side items on one field
 * would collide last-write-wins, but the contract guarantees at most one per side.
 */
export const modelToCrmFields = (
    dto: CrmFiltersModel | undefined,
    provider: CrmIntegrationProvider,
): Readonly<CrmFiltersList> => {
    if (!dto || !Array.isArray(dto) || dto.length === 0) {
        return {};
    }

    const flat: Record<string, DynamicFilterValue> = {};

    for (const item of dto) {
        let inclusion: InclusionOption = inclusionOptionMap.includeOnly;
        const fieldEntries: [string, DynamicFilterValue][] = [];

        for (const [key, value] of Object.entries(item)) {
            if (key === INCLUSION_FIELD) {
                inclusion = value as InclusionOption;
                continue;
            }

            fieldEntries.push([key, value as DynamicFilterValue]);
        }

        for (const [fieldKey, fieldValue] of fieldEntries) {
            const parsed = parseDynamicFilterValue(fieldValue);

            if (parsed) {
                flat[crmConditionVariantKey(toCrmFilterKey(provider, fieldKey), inclusion)] = parsed;
            }
        }
    }

    return flat;
};
